import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FormsModule } from '@angular/forms';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService } from 'primeng/api';

import { routes } from './app.routes';
import { authInterceptor } from './core/auth.interceptor';
import { connectivityInterceptor } from './core/connectivity.interceptor';
import { DEFAULT_LOCALE } from './core/locale.service';
import { InventoPreset } from './core/prime-preset';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    ConfirmationService,
    provideHttpClient(
      withInterceptors([authInterceptor, connectivityInterceptor]),
    ),
    // provideTranslateHttpLoader must come after provideTranslateService — it overrides the
    // TranslateNoOpLoader that provideTranslateService registers by default when no loader is given.
    provideTranslateService({
      lang: DEFAULT_LOCALE,
      fallbackLang: DEFAULT_LOCALE,
    }),
    provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
    provideAnimationsAsync(),
    // Every PrimeNG form component (p-select, p-checkbox, p-radioButton, ...) is a
    // ControlValueAccessor. Angular's default forms behavior calls `setDisabledState()` on that
    // accessor as soon as `[ngModel]` is set up — which unconditionally forces its `disabled` flag
    // back to the FormControl's own (always-enabled) state, silently clobbering a plain
    // `[disabled]="expr"` binding placed alongside `[ngModel]` on the same element. This restores
    // the pre-forms-refactor behavior — setDisabledState only fires for a control explicitly
    // disabled via the forms API — so a manual `[disabled]` binding stays the source of truth
    // everywhere in the app, exactly like it did on a plain native <select disabled>.
    importProvidersFrom(FormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' })),
    providePrimeNG({
      theme: {
        preset: InventoPreset,
        options: {
          // Puts every PrimeNG rule in a `primeng` cascade layer, so our own (unlayered)
          // component styles always win regardless of selector specificity.
          cssLayer: { name: 'primeng', order: 'primeng' },
        },
      },
    }),
  ],
};
